import React, { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import styled from 'styled-components';
import { useDropzone } from 'react-dropzone';
import { Modal } from 'src/components/modals/comps';
import Input, { Textarea } from 'src/components/form/Input';
import { CreatableSelect, MultiSelect } from 'src/components/form/Select';
import Button from 'src/components/form/Button';
import { InputLabel, Poppins, Spacer } from 'src/common';
import { CsvValidationErrors, SCVSummary } from 'src/components/CsvUpload';
import { parseGraphqlCsvValidationErrors, ValidationErrorItem } from 'src/utils/csv';
import { showSuccessToast } from 'src/components/Toast';
import { useAuth } from 'src/state/auth';
import { Stepper } from 'src/components/Wizzard/comps';
import {
  CREATE_FRAMEWORK_FROM_IMPORT,
  CreateFrameworkFromImportMutation,
  CreateFrameworkFromImportMutationVariables,
  UPSERT_CONTENT_PACK_ACCESS,
  UpsertContentPackAccessMutation,
  UpsertContentPackAccessMutationVariables,
} from 'src/graphql/contentImport';
import { FrameworkImportPayload } from 'src/types/contentImport';
import { generateFrameworkTemplateLink, parseFrameworkCsv } from 'src/pages/FrameworkLibrabryPage/import';

const StyledModal = styled(Modal)`
  width: 820px;
  padding: 0 !important;
`;

const Content = styled.div`
  padding: 34px 30px 30px;
`;

const StepperWrap = styled.div`
  padding: 0 2px;
`;

const StepPanel = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.white};
  padding: 20px;
`;

const TemplateHint = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.cultured};
  padding: 14px 18px;

  a {
    color: ${({ theme }) => theme.colors.error};
    text-decoration: none;
    font-weight: 500;
  }
`;

const FormInput = styled(Input)`
  &[type='text'] {
    padding: 0 18px;
  }
`;

const FormTextarea = styled(Textarea)`
  min-height: 118px;
  padding: 14px 18px;
`;

const UploadBox = styled.div<{ $dragActive?: boolean }>`
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  border-radius: 4px;
  background: ${({ theme, $dragActive }) => ($dragActive ? theme.colors.stroke : theme.colors.cultured)};
  min-height: 128px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 16px;
  cursor: pointer;
  transition: background 120ms ease-in-out;
`;

const SummaryCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.cultured};
  padding: 16px 18px;
`;

const PublishGrid = styled.div`
  display: grid;
  gap: 18px;
`;

const ButtonsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    flex-shrink: 0;
  }

  .end {
    margin-left: auto;
  }
`;

const PrimaryActionButton = styled(Button)`
  width: auto;
  min-width: max-content;
  padding-left: 2rem;
  padding-right: 2rem;
`;

const SummaryRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  ${({ theme }) => theme.breakpoints.down('sm')} {
    grid-template-columns: 1fr;
  }
`;

interface OrgUnitOption {
  value: string;
  label: string;
}

interface PackOwnerOption {
  value: string;
  label: string;
  organisation?: unknown | null;
}

type AddControlFrameworkStepId = 'create' | 'edit' | 'publish';

interface AddControlFrameworkModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => Promise<void> | void;
  ownerOptions?: PackOwnerOption[];
  defaultOwnerId?: string | null;
  showOwnerSelector?: boolean;
}

const CYDEA_OWNER_VALUE = '__cydea__';
const normaliseOwnerId = (value?: string | null) => (value && value !== CYDEA_OWNER_VALUE ? value : null);
const EMPTY_OWNER_OPTIONS: PackOwnerOption[] = [];

export const AddControlFrameworkModal: React.FC<AddControlFrameworkModalProps> = ({
  isOpen,
  onClose,
  onCreated,
  ownerOptions = EMPTY_OWNER_OPTIONS,
  defaultOwnerId = null,
  showOwnerSelector = false,
}) => {
  const { flatOrganisations, workspaceId } = useAuth();

  const [step, setStep] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [frameworkName, setFrameworkName] = useState('');
  const [shortName, setShortName] = useState('');
  const [description, setDescription] = useState('');
  const [author, setAuthor] = useState('');
  const [ownerOrganisationId, setOwnerOrganisationId] = useState<string | null>(defaultOwnerId);
  const [frameworkPayload, setFrameworkPayload] = useState<FrameworkImportPayload | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationErrorItem[]>([]);
  const [createdCount, setCreatedCount] = useState(0);
  const [updatedCount, setUpdatedCount] = useState(0);
  const [selectedOrganisationIds, setSelectedOrganisationIds] = useState<string[]>([]);
  const [selectedWorkspaceIds, setSelectedWorkspaceIds] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [isParsing, setIsParsing] = useState(false);

  const [runCreateFramework, { loading: isCreating }] = useMutation<
    CreateFrameworkFromImportMutation,
    CreateFrameworkFromImportMutationVariables
  >(CREATE_FRAMEWORK_FROM_IMPORT);

  const [runUpsertContentPackAccess, { loading: isPublishingTargets }] = useMutation<
    UpsertContentPackAccessMutation,
    UpsertContentPackAccessMutationVariables
  >(UPSERT_CONTENT_PACK_ACCESS);

  const isSubmitting = isCreating || isPublishingTargets;
  const templateLink = useMemo(() => generateFrameworkTemplateLink(), []);

  useEffect(() => {
    return () => {
      URL.revokeObjectURL(templateLink);
    };
  }, [templateLink]);

  const organisationOptions = useMemo<OrgUnitOption[]>(() => {
    return (flatOrganisations || [])
      .filter((org) => org.recordType === 'O' && !org.isArchived)
      .map((org) => ({
        value: org.id,
        label: org.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [flatOrganisations]);

  const workspaceOptions = useMemo<OrgUnitOption[]>(() => {
    return (flatOrganisations || [])
      .filter((org) => org.recordType === 'W' && !org.isArchived)
      .map((org) => ({
        value: org.id,
        label: org.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [flatOrganisations]);

  const resetState = () => {
    setStep(0);
    setFile(null);
    setFrameworkName('');
    setShortName('');
    setDescription('');
    setAuthor(showOwnerSelector ? ownerOptions.find((option) => option.value === defaultOwnerId)?.label || 'Cydea' : '');
    setOwnerOrganisationId(defaultOwnerId);
    setFrameworkPayload(null);
    setValidationErrors([]);
    setCreatedCount(0);
    setUpdatedCount(0);
    setSelectedOrganisationIds([]);
    setSelectedWorkspaceIds(workspaceId ? [workspaceId] : []);
    setError('');
    setIsParsing(false);
  };

  useEffect(() => {
    if (!isOpen) return;
    resetState();
  }, [isOpen, workspaceId, defaultOwnerId, showOwnerSelector]);

  const handleClose = () => {
    if (isSubmitting) return;
    resetState();
    onClose();
  };

  const onDrop = async (acceptedFiles: File[]) => {
    const selectedFile = acceptedFiles[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setError('');
    setIsParsing(true);
    setFrameworkPayload(null);
    setValidationErrors([]);
    setCreatedCount(0);
    setUpdatedCount(0);

    try {
      const parsed = await parseFrameworkCsv({
        file: selectedFile,
        existingControlIds: [],
        requireFrameworkName: false,
      });

      setFrameworkPayload(parsed.payload);
      setValidationErrors(parsed.validationErrors);
      setCreatedCount(parsed.createdCount);
      setUpdatedCount(parsed.updatedCount);

      if (parsed.payload?.framework?.name && !frameworkName.trim()) {
        setFrameworkName(parsed.payload.framework.name);
      }

      if (parsed.payload?.framework?.shortname && !shortName.trim()) {
        setShortName(parsed.payload.framework.shortname);
      }

      if (parsed.payload?.framework?.description && !description.trim()) {
        setDescription(parsed.payload.framework.description);
      }

      if (parsed.payload?.framework?.author && !author.trim()) {
        setAuthor(parsed.payload.framework.author);
      }
    } finally {
      setIsParsing(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: { 'text/csv': ['.csv'] },
    disabled: isSubmitting,
  });

  const hasValidatedFile = !!frameworkPayload && !validationErrors.length && !isParsing;
  const canProceedToEdit = hasValidatedFile && !!frameworkName.trim() && !isSubmitting;
  const canProceedToPublish = canProceedToEdit;
  const hasPublishTargets = selectedOrganisationIds.length > 0 || selectedWorkspaceIds.length > 0;
  const canPublish = canProceedToPublish && hasPublishTargets && !isSubmitting;

  const slides = useMemo<{ title: string; id: AddControlFrameworkStepId; locked?: boolean }[]>(
    () => [
      {
        title: 'Create framework',
        id: 'create',
      },
      {
        title: 'Review details',
        id: 'edit',
        locked: !canProceedToEdit,
      },
      {
        title: 'Publish',
        id: 'publish',
        locked: !canProceedToPublish,
      },
    ],
    [canProceedToEdit, canProceedToPublish],
  );

  const currentStepId = slides[step]?.id;
  const selectedOwner =
    (ownerOrganisationId
      ? ownerOptions.find((option) => option.value === ownerOrganisationId)
      : author.trim() && author.trim().toLowerCase() !== 'cydea'
      ? { value: author.trim(), label: author.trim(), organisation: null }
      : null) ||
    ownerOptions.find((option) => option.value === CYDEA_OWNER_VALUE || option.value === '') ||
    ownerOptions[0] ||
    null;
  const selectedAuthorName = showOwnerSelector ? selectedOwner?.label || 'Cydea' : author.trim() || null;

  const onChangeSlide = (id: AddControlFrameworkStepId) => {
    const idx = slides.findIndex((slide) => slide.id === id);
    if (idx < 0 || slides[idx].locked) return;
    setStep(idx);
    setError('');
  };

  const publishFramework = async () => {
    if (!frameworkPayload || !frameworkName.trim() || !hasPublishTargets) return;

    setError('');

    const input: FrameworkImportPayload = {
      ...frameworkPayload,
      framework: {
        ...frameworkPayload.framework,
        id: null,
        organisationId: showOwnerSelector && selectedOwner?.organisation ? normaliseOwnerId(ownerOrganisationId) : null,
        name: frameworkName.trim(),
        shortname: shortName.trim() || null,
        description: description.trim() || null,
        author: selectedAuthorName,
      },
    };

    try {
      const { data } = await runCreateFramework({
        variables: {
          input,
        },
      });

      const summary = data?.createFrameworkFromImport;
      if (!summary?.frameworkId) {
        throw new Error('Control framework publish failed.');
      }

      await runUpsertContentPackAccess({
        variables: {
          packType: 'framework',
          packId: summary.frameworkId,
          organisations: selectedOrganisationIds,
          workspaces: selectedWorkspaceIds,
        },
      });

      await onCreated();
      showSuccessToast({ title: 'Control framework published' });
      handleClose();
    } catch (err) {
      const parsedValidationErrors = parseGraphqlCsvValidationErrors(err);
      if (parsedValidationErrors.length > 0) {
        setValidationErrors(parsedValidationErrors);
        setStep(0);
      }

      setError(err instanceof Error ? err.message : 'Control framework publish failed.');
    }
  };

  const onNext = async () => {
    if (currentStepId === 'create') {
      if (!canProceedToEdit) return;
      setStep(1);
      return;
    }

    if (currentStepId === 'edit') {
      if (!canProceedToPublish) return;
      setStep(2);
      return;
    }

    await publishFramework();
  };

  const nextDisabled =
    (currentStepId === 'create' && !canProceedToEdit) ||
    (currentStepId === 'edit' && !canProceedToPublish) ||
    (currentStepId === 'publish' && !canPublish);

  return (
    <StyledModal isOpen={isOpen} onRequestClose={handleClose} persistent={isSubmitting}>
      <Content>
        <Poppins className="m-title" px={32}>
          Add control framework
        </Poppins>

        <Spacer $px={18} />
        <StepperWrap>
          <Stepper slides={slides} step={step} onChangeSlide={onChangeSlide} maxSteps={3} />
        </StepperWrap>

        <Spacer $px={18} />
        <StepPanel>
          {currentStepId === 'create' && (
            <>
              <TemplateHint>
                <Poppins px={14}>
                  <a href={templateLink} download="framework_template.csv">
                    Click here
                  </a>{' '}
                  to download the CSV template for control framework
                </Poppins>
              </TemplateHint>

              <Spacer $px={20} />
              <InputLabel>NAME</InputLabel>
              <Spacer $px={8} />
              <FormInput
                value={frameworkName}
                onChange={(e) => setFrameworkName(e.target.value)}
                placeholder="Enter control framework name"
                maxLength={255}
              />

              <Spacer $px={16} />
              <InputLabel>SHORT NAME</InputLabel>
              <Spacer $px={8} />
              <FormInput
                value={shortName}
                onChange={(e) => setShortName(e.target.value)}
                placeholder="Enter short name"
                maxLength={255}
              />

              <Spacer $px={16} />
              <InputLabel>DESCRIPTION</InputLabel>
              <Spacer $px={8} />
              <FormTextarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe this control framework"
                maxLength={3000}
              />

              <Spacer $px={16} />
              <InputLabel>AUTHOR</InputLabel>
              <Spacer $px={8} />
              {showOwnerSelector ? (
                <CreatableSelect
                  options={ownerOptions}
                  value={selectedOwner}
                  onChange={(nextValue: unknown) => {
                    const nextOwner = nextValue as PackOwnerOption | null;
                    setOwnerOrganisationId(nextOwner?.organisation ? normaliseOwnerId(nextOwner?.value) : null);
                    setAuthor(nextOwner?.label || 'Cydea');
                  }}
                  placeholder="Type in or select author"
                  isDisabled={isSubmitting}
                  isSearchable
                  formatCreateLabel={(value: string) => value}
                />
              ) : (
                <FormInput
                  value={author}
                  onChange={(e) => setAuthor(e.target.value)}
                  placeholder="Enter author"
                  maxLength={255}
                  disabled={isSubmitting}
                />
              )}

              <Spacer $px={16} />
              <InputLabel>UPLOAD A CSV FILE</InputLabel>
              <Spacer $px={8} />
              <UploadBox {...getRootProps()} $dragActive={isDragActive}>
                <input {...getInputProps()} />
                <Poppins px={16} weight={500}>
                  {file
                    ? `File selected: ${file.name}`
                    : isDragActive
                    ? 'Drop the CSV file here'
                    : 'Click to upload or drag and drop CSV file'}
                </Poppins>
              </UploadBox>

              {isParsing && (
                <>
                  <Spacer $px={10} />
                  <Poppins px={14}>Validating CSV file...</Poppins>
                </>
              )}

              <Spacer $px={16} />
              <CsvValidationErrors errors={validationErrors} />

              {hasValidatedFile && (
                <SummaryCard>
                  <SCVSummary
                    items={[
                      'File validated',
                      `${createdCount} controls ready to be created`,
                      updatedCount ? `${updatedCount} controls ready to be updated` : '',
                    ].filter(Boolean)}
                  />
                </SummaryCard>
              )}
            </>
          )}

          {currentStepId === 'edit' && (
            <>
              <InputLabel>NAME</InputLabel>
              <Spacer $px={8} />
              <FormInput
                value={frameworkName}
                onChange={(e) => setFrameworkName(e.target.value)}
                placeholder="Enter control framework name"
                maxLength={255}
              />

              <Spacer $px={16} />
              <InputLabel>SHORT NAME</InputLabel>
              <Spacer $px={8} />
              <FormInput
                value={shortName}
                onChange={(e) => setShortName(e.target.value)}
                placeholder="Enter short name"
                maxLength={255}
              />

              <Spacer $px={16} />
              <InputLabel>DESCRIPTION</InputLabel>
              <Spacer $px={8} />
              <FormTextarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe this control framework"
                maxLength={3000}
              />

              <Spacer $px={16} />
              <InputLabel>AUTHOR</InputLabel>
              <Spacer $px={8} />
              {showOwnerSelector ? (
                <CreatableSelect
                  options={ownerOptions}
                  value={selectedOwner}
                  onChange={(nextValue: unknown) => {
                    const nextOwner = nextValue as PackOwnerOption | null;
                    setOwnerOrganisationId(nextOwner?.organisation ? normaliseOwnerId(nextOwner?.value) : null);
                    setAuthor(nextOwner?.label || 'Cydea');
                  }}
                  placeholder="Type in or select author"
                  isDisabled={isSubmitting}
                  isSearchable
                  formatCreateLabel={(value: string) => value}
                />
              ) : (
                <FormInput
                  value={author}
                  onChange={(e) => setAuthor(e.target.value)}
                  placeholder="Enter author"
                  maxLength={255}
                  disabled={isSubmitting}
                />
              )}

              <Spacer $px={16} />
              <SummaryCard>
                <SummaryRow>
                  <div>
                    <InputLabel>CSV FILE</InputLabel>
                    <Spacer $px={4} />
                    <Poppins px={14}>{file?.name || 'No file selected'}</Poppins>
                  </div>
                  <div>
                    <InputLabel>CONTROLS</InputLabel>
                    <Spacer $px={4} />
                    <Poppins px={14}>{createdCount + updatedCount}</Poppins>
                  </div>
                </SummaryRow>
              </SummaryCard>
            </>
          )}

          {currentStepId === 'publish' && (
            <PublishGrid>
              <SummaryCard>
                <SummaryRow>
                  <div>
                    <InputLabel>CONTROL FRAMEWORK</InputLabel>
                    <Spacer $px={4} />
                    <Poppins px={14} weight={500}>
                      {frameworkName.trim()}
                    </Poppins>
                  </div>
                  <div>
                    <InputLabel>CONTROLS</InputLabel>
                    <Spacer $px={4} />
                    <Poppins px={14}>{createdCount + updatedCount}</Poppins>
                  </div>
                </SummaryRow>
              </SummaryCard>

              <div>
                <InputLabel>PUBLISH TO ORGANISATIONS</InputLabel>
                <Spacer $px={8} />
                <MultiSelect
                  options={organisationOptions}
                  value={selectedOrganisationIds}
                  onChange={setSelectedOrganisationIds}
                  placeholder="Select organisations"
                  selectedLabel="selected"
                />
              </div>

              <div>
                <InputLabel>PUBLISH TO WORKSPACES</InputLabel>
                <Spacer $px={8} />
                <MultiSelect
                  options={workspaceOptions}
                  value={selectedWorkspaceIds}
                  onChange={setSelectedWorkspaceIds}
                  placeholder="Select workspaces"
                  selectedLabel="selected"
                />
              </div>

              {!hasPublishTargets && (
                <Poppins px={14} color="error">
                  Select at least one organisation or workspace to publish this control framework.
                </Poppins>
              )}
            </PublishGrid>
          )}
        </StepPanel>

        {!!error && (
          <>
            <Spacer $px={14} />
            <Poppins px={14} color="error">
              {error}
            </Poppins>
          </>
        )}

        <Spacer $px={20} />
        <ButtonsRow>
          {step > 0 ? (
            <Button secondary onClick={() => setStep((prev) => Math.max(0, prev - 1))} disabled={isSubmitting}>
              Previous
            </Button>
          ) : (
            <div />
          )}

          <PrimaryActionButton
            primary
            className="end"
            onClick={onNext}
            disabled={nextDisabled}
            loading={isSubmitting}
          >
            {currentStepId === 'publish' ? 'Publish control framework' : 'Next'}
          </PrimaryActionButton>
        </ButtonsRow>
      </Content>
    </StyledModal>
  );
};
